import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatMoeda } from '../../utils/fmt';
import { resolverPreco } from '../../utils/precoModelo';
import * as api from '../../api/carrinho';
import * as modelosApi from '../../api/modelos';
import * as estoqueApi from '../../api/estoque';
import * as enderecosApi from '../../api/enderecos';
import * as coresApi from '../../api/cores';
import * as tamanhosApi from '../../api/tamanhos';

/* ─── Utilidades de CEP ─── */
function formatCep(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/* ─── Componente de card de endereço salvo ─── */
function CardEndereco({ end, onUsar }) {
  const cepFmt = end.cep.length === 8 ? `${end.cep.slice(0, 5)}-${end.cep.slice(5)}` : end.cep;
  return (
    <div style={{
      border: '2px solid var(--cjc-primary)',
      borderRadius: 8,
      padding: '20px 24px',
      background: '#f8f8ff',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'var(--cjc-accent)', textTransform: 'uppercase', marginBottom: 6 }}>
        Endereço salvo
      </div>
      <div className="fw-semibold" style={{ fontSize: 15, color: 'var(--cjc-primary)', lineHeight: 1.4 }}>
        {end.logradouro}, {end.numero}{end.complemento && `, ${end.complemento}`}
      </div>
      <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
        {end.bairro} — {end.cidade}/{end.uf}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
        CEP {cepFmt}
      </div>
      {end.ponto_referencia && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
          📍 {end.ponto_referencia}
        </div>
      )}
      <button
        className="btn btn-primary mt-3 w-100"
        onClick={() => onUsar(end)}
      >
        Entregar neste endereço →
      </button>
    </div>
  );
}

/* ─── Formulário de novo endereço com CEP ─── */
function FormEndereco({ onConfirmar, onVoltar, voltarLabel, fechando, total }) {
  const [cepRaw, setCepRaw] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState('');
  const [viaCep, setViaCep] = useState(null); // { logradouro, bairro, cidade, uf, semLogradouro }
  const [logradouroManual, setLogradouroManual] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [pontoRef, setPontoRef] = useState('');
  const [salvar, setSalvar] = useState(true);
  const numeroRef = useRef(null);

  const buscarCep = async (cepLimpo) => {
    setBuscandoCep(true);
    setErroCep('');
    setViaCep(null);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (data.erro) {
        setErroCep('CEP não encontrado. Verifique e tente novamente.');
      } else {
        setViaCep({
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          uf: data.uf || '',
          semLogradouro: !data.logradouro,
        });
        setLogradouroManual(data.logradouro || '');
        setTimeout(() => numeroRef.current?.focus(), 50);
      }
    } catch {
      setErroCep('Erro ao consultar CEP. Verifique sua conexão.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleCepChange = (e) => {
    const limpo = e.target.value.replace(/\D/g, '').slice(0, 8);
    setCepRaw(limpo);
    if (viaCep) { setViaCep(null); setLogradouroManual(''); }
    setErroCep('');
    if (limpo.length === 8) buscarCep(limpo);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!viaCep) return;
    if (!numero.trim()) { numeroRef.current?.focus(); return; }
    onConfirmar({
      cep: cepRaw,
      logradouro: logradouroManual.trim(),
      numero: numero.trim(),
      complemento: complemento.trim() || null,
      bairro: viaCep.bairro,
      cidade: viaCep.cidade,
      uf: viaCep.uf,
      ponto_referencia: pontoRef.trim() || null,
      salvar_endereco: salvar,
    });
  };

  const cepFormatado = formatCep(cepRaw);
  const enderecoCompleto = viaCep && numero.trim();

  return (
    <form onSubmit={handleSubmit}>

      {/* CEP — campo principal, destaque visual */}
      <div className="mb-4">
        <label className="form-label fw-bold" style={{ fontSize: 13 }}>CEP *</label>
        <div className="d-flex gap-2 align-items-center">
          <input
            type="text"
            inputMode="numeric"
            className={`form-control form-control-lg ${erroCep ? 'is-invalid' : viaCep ? 'is-valid' : ''}`}
            style={{ maxWidth: 180, letterSpacing: 2, fontWeight: 600, fontSize: 18 }}
            placeholder="00000-000"
            value={cepFormatado}
            onChange={handleCepChange}
            autoFocus
          />
          {buscandoCep && (
            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13 }}>
              <div className="spinner-border spinner-border-sm" style={{ width: 16, height: 16 }} />
              Buscando...
            </div>
          )}
          {viaCep && !buscandoCep && (
            <span style={{ fontSize: 13, color: '#198754' }}>✓ Endereço encontrado</span>
          )}
        </div>
        {erroCep && <div className="text-danger mt-1" style={{ fontSize: 13 }}>{erroCep}</div>}
        {!viaCep && !erroCep && (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            Digite o CEP e o endereço será preenchido automaticamente.
          </div>
        )}
      </div>

      {/* Campos preenchidos pelo ViaCEP */}
      {viaCep && (
        <div
          className="mb-3 rounded p-3"
          style={{ background: '#f0f4ff', border: '1px solid #d0d8f0', animation: 'fadeIn .2s' }}
        >
          {/* Logradouro — editável se viaCEP não retornou */}
          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>
              Logradouro {viaCep.semLogradouro ? '*' : ''}
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={logradouroManual}
              onChange={e => setLogradouroManual(e.target.value)}
              readOnly={!viaCep.semLogradouro}
              required
              style={!viaCep.semLogradouro ? { background: '#e8ecf8', color: '#333' } : {}}
            />
          </div>

          <div className="row g-2 mb-3">
            <div className="col-5">
              <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>Bairro</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={viaCep.bairro}
                readOnly
                style={{ background: '#e8ecf8', color: '#333' }}
              />
            </div>
            <div className="col-5">
              <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>Cidade</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={viaCep.cidade}
                readOnly
                style={{ background: '#e8ecf8', color: '#333' }}
              />
            </div>
            <div className="col-2">
              <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>UF</label>
              <input
                type="text"
                className="form-control form-control-sm text-center fw-bold"
                value={viaCep.uf}
                readOnly
                style={{ background: '#e8ecf8', color: '#333' }}
              />
            </div>
          </div>

          {/* Número + Complemento */}
          <div className="row g-2 mb-3">
            <div className="col-4">
              <label className="form-label fw-bold" style={{ fontSize: 12, color: 'var(--cjc-primary)' }}>
                Número *
              </label>
              <input
                ref={numeroRef}
                type="text"
                className="form-control form-control-sm"
                value={numero}
                onChange={e => setNumero(e.target.value)}
                placeholder="Ex: 123"
                required
                autoComplete="off"
              />
            </div>
            <div className="col-8">
              <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>
                Complemento
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={complemento}
                onChange={e => setComplemento(e.target.value)}
                placeholder="Apto, Sala, Bloco... (opcional)"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Ponto de referência */}
          <div>
            <label className="form-label fw-semibold" style={{ fontSize: 12, color: '#444' }}>
              Ponto de referência
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={pontoRef}
              onChange={e => setPontoRef(e.target.value)}
              placeholder="Ex: Próximo ao mercado, portão azul... (opcional)"
              autoComplete="off"
            />
          </div>
        </div>
      )}

      {/* Salvar endereço */}
      {viaCep && (
        <div
          className="form-check mb-4 p-3 rounded"
          style={{ background: '#fffbf0', border: '1px solid #ffe08a', cursor: 'pointer' }}
          onClick={() => setSalvar(s => !s)}
        >
          <input
            className="form-check-input"
            type="checkbox"
            id="salvarEndereco"
            checked={salvar}
            onChange={e => setSalvar(e.target.checked)}
            onClick={e => e.stopPropagation()}
          />
          <label className="form-check-label ms-1" htmlFor="salvarEndereco" style={{ cursor: 'pointer', fontSize: 13 }}>
            <span className="fw-semibold">Salvar endereço para próximas entregas</span>
            <span className="text-muted d-block" style={{ fontSize: 12 }}>
              Na próxima compra, este endereço aparecerá automaticamente.
            </span>
          </label>
        </div>
      )}

      <div className="d-flex gap-2">
        <button type="button" className="btn btn-outline-secondary" onClick={onVoltar}>
          {voltarLabel}
        </button>
        <button
          type="submit"
          className="btn btn-primary flex-grow-1"
          disabled={!enderecoCompleto || fechando}
        >
          {fechando
            ? <><span className="spinner-border spinner-border-sm me-2" />Confirmando...</>
            : `Confirmar Pedido — ${formatMoeda(total)}`}
        </button>
      </div>
    </form>
  );
}

/* ─── Componente principal ─── */
export default function Carrinho() {
  const [itens, setItens]       = useState([]);
  const [modelos, setModelos]   = useState([]);
  const [estoque, setEstoque]   = useState([]);
  const [cores, setCores]       = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [step, setStep]         = useState('carrinho');
  const [fechando, setFechando] = useState(false);
  const [pedidoFechado, setPedidoFechado] = useState(null);
  const [erroCarregar, setErroCarregar] = useState('');

  const [enderecosSalvos, setEnderecosSalvos]   = useState([]);
  const [loadingEnd, setLoadingEnd]             = useState(false);
  const [usarNovoEndereco, setUsarNovoEndereco] = useState(false);

  const [areas, setAreas] = useState([]);
  const [idAreaEscolhida, setIdAreaEscolhida] = useState('');

  const navigate = useNavigate();

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listarItens(), modelosApi.listar(), estoqueApi.listar(), coresApi.listar(), tamanhosApi.listar(), api.areasDisponiveis()])
      .then(([i, m, e, c, tam, a]) => { setItens(i.data); setModelos(m.data); setEstoque(e.data); setCores(c.data); setTamanhos(tam.data); setAreas(a.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const entrarStepEndereco = useCallback(() => {
    setStep('endereco');
    setUsarNovoEndereco(false);
    setLoadingEnd(true);
    enderecosApi.listar()
      .then(r => setEnderecosSalvos(r.data))
      .catch(() => setEnderecosSalvos([]))
      .finally(() => setLoadingEnd(false));
  }, []);

  // Área é escolhida 1x pro pedido inteiro — só precisa perguntar quando há 2+
  // candidatas (com 0 ou 1, o backend resolve sozinho ao fechar).
  const irParaProximoPasso = () => {
    if (areas.length > 1) setStep('area');
    else entrarStepEndereco();
  };

  const modeloInfo    = id => modelos.find(m => m.id_modelo_uniforme === id);
  const corNome       = id => cores.find(c => c.id_cor === id)?.nome;
  const tamanhoNome   = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;
  const estoqueDisp   = (id, idTamanho, idCor) =>
    estoque.find(e => e.id_modelo_uniforme === id && e.id_tamanho_uniforme === idTamanho && (e.id_cor || null) === (idCor || null))
      ?.quantidade_disponivel ?? 0;

  const total = itens.reduce((s, item) => {
    const m = modeloInfo(item.id_modelo_uniforme);
    return s + resolverPreco(m, item.id_tamanho_uniforme) * item.quantidade;
  }, 0);

  const totalFabricar = itens.reduce((s, item) => {
    const disp = estoqueDisp(item.id_modelo_uniforme, item.id_tamanho_uniforme, item.id_cor);
    return s + Math.max(0, item.quantidade - disp);
  }, 0);

  const atualizar = async (item, novaQtd) => {
    if (novaQtd < 1) return;
    await api.upsertItem({
      id_modelo_uniforme: item.id_modelo_uniforme, id_tamanho_uniforme: item.id_tamanho_uniforme, id_cor: item.id_cor,
      quantidade: novaQtd,
    });
    carregar();
  };

  const remover = async (id) => {
    await api.removerItem(id);
    carregar();
  };

  const fecharComEnderecoSalvo = async (endObj) => {
    setFechando(true);
    try {
      const { data } = await api.fecharPedido({ id_endereco: endObj.id_endereco, id_area_cliente: idAreaEscolhida || undefined });
      setPedidoFechado(data);
      setStep('confirmado');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao fechar pedido.');
    } finally { setFechando(false); }
  };

  const fecharComNovoEndereco = async (endData) => {
    const { salvar_endereco, ...enderecoFields } = endData;
    setFechando(true);
    try {
      const { data } = await api.fecharPedido({ endereco: enderecoFields, salvar_endereco, id_area_cliente: idAreaEscolhida || undefined });
      setPedidoFechado(data);
      setStep('confirmado');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao fechar pedido.');
    } finally { setFechando(false); }
  };

  if (loading) return <LoadingSpinner />;

  /* ── Tela de sucesso ── */
  if (step === 'confirmado' && pedidoFechado) {
    return (
      <div>
        <PageHeader titulo="Pedido Confirmado!" />
        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-body text-center py-5">
            <div style={{ fontSize: 52 }}>✅</div>
            <h4 style={{ color: 'var(--cjc-primary)', marginTop: 12 }}>{pedidoFechado.numero_pedido}</h4>
            <p className="text-muted mb-1">Seu pedido foi recebido com sucesso.</p>
            <p className="fw-bold fs-5 mb-3">{formatMoeda(pedidoFechado.valor_total)}</p>
            {pedidoFechado.endereco_entrega && (
              <div className="alert alert-light text-start py-2 mb-3" style={{ fontSize: 13 }}>
                <strong>Endereço de entrega:</strong><br />
                {pedidoFechado.endereco_entrega}
              </div>
            )}
            {totalFabricar > 0 && (
              <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 13 }}>
                ⚠ {totalFabricar} unidade(s) entrarão em fila de fabricação e serão entregues em seguida.
              </div>
            )}
            <button className="btn btn-primary mt-1" onClick={() => navigate('/meus-pedidos')}>
              Ver Meus Pedidos
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Tela de área (só aparece com 2+ áreas candidatas) ── */
  if (step === 'area') {
    return (
      <div>
        <PageHeader
          titulo="Área do Pedido"
          subtitulo="Para qual área este pedido é destinado?"
        />
        <div style={{ maxWidth: 480 }}>
          <div className="d-flex flex-column gap-2 mb-3">
            {areas.map(a => (
              <button
                key={a.id_area_cliente}
                type="button"
                className={`btn text-start ${idAreaEscolhida === a.id_area_cliente ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setIdAreaEscolhida(a.id_area_cliente)}
              >
                {a.nome}
              </button>
            ))}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => setStep('carrinho')}>
              ← Voltar ao Carrinho
            </button>
            <button className="btn btn-primary flex-grow-1" disabled={!idAreaEscolhida} onClick={entrarStepEndereco}>
              Continuar →
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Tela de endereço ── */
  if (step === 'endereco') {
    const endPrincipal = enderecosSalvos[0];
    const mostrarSalvo = endPrincipal && !usarNovoEndereco;

    return (
      <div>
        <PageHeader
          titulo="Endereço de Entrega"
          subtitulo="Para onde devemos enviar o pedido?"
        />
        <div style={{ maxWidth: 560 }}>
          {loadingEnd ? (
            <LoadingSpinner />
          ) : mostrarSalvo ? (
            /* ── Endereço salvo encontrado ── */
            <div>
              <CardEndereco end={endPrincipal} onUsar={fecharComEnderecoSalvo} />

              <div className="text-center mt-3">
                <button
                  className="btn btn-link text-muted p-0"
                  style={{ fontSize: 13 }}
                  onClick={() => setUsarNovoEndereco(true)}
                >
                  Usar outro endereço
                </button>
              </div>

              <div className="mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setStep('carrinho')}
                >
                  ← Voltar ao Carrinho
                </button>
              </div>
            </div>
          ) : (
            /* ── Formulário de novo endereço ── */
            <div>
              {enderecosSalvos.length > 0 && (
                <div className="mb-3">
                  <button
                    className="btn btn-link p-0 text-muted"
                    style={{ fontSize: 13 }}
                    onClick={() => setUsarNovoEndereco(false)}
                  >
                    ← Usar endereço salvo
                  </button>
                </div>
              )}
              <div className="card">
                <div className="card-body">
                  <FormEndereco
                    onConfirmar={fecharComNovoEndereco}
                    onVoltar={() => {
                      if (enderecosSalvos.length > 0) setUsarNovoEndereco(false);
                      else setStep('carrinho');
                    }}
                    voltarLabel={enderecosSalvos.length > 0 ? '← Endereço salvo' : '← Voltar ao Carrinho'}
                    fechando={fechando}
                    total={total}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Tela do carrinho ── */
  return (
    <div>
      <PageHeader titulo="Carrinho" subtitulo={`${itens.length} item(ns)`} />

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      {itens.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">Seu carrinho está vazio.</p>
          <button className="btn btn-primary" onClick={() => navigate('/catalogo')}>
            Ver Catálogo
          </button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body p-0">
                {itens.map(item => {
                  const m = modeloInfo(item.id_modelo_uniforme);
                  const disp = estoqueDisp(item.id_modelo_uniforme, item.id_tamanho_uniforme, item.id_cor);
                  const aFabricar = Math.max(0, item.quantidade - disp);
                  const foto = m?.fotos?.[0]?.url;
                  const preco = resolverPreco(m, item.id_tamanho_uniforme);

                  return (
                    <div key={item.id_carrinho_item}
                      className="d-flex align-items-center gap-3 p-3"
                      style={{ borderBottom: '1px solid var(--cjc-gray-200)' }}>
                      {foto ? (
                        <img src={foto} alt={m?.nome}
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 64, height: 64, background: 'var(--cjc-gray-100)',
                          borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 10, color: 'var(--cjc-gray-400)' }}>sem foto</span>
                        </div>
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-semibold">{m?.nome || item.id_modelo_uniforme}</div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <span className="badge bg-secondary">{tamanhoNome(item.id_tamanho_uniforme)}</span>
                          {item.id_cor && corNome(item.id_cor) && (
                            <span className="badge bg-light text-dark border">{corNome(item.id_cor)}</span>
                          )}
                          {aFabricar > 0 && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>
                              ⚠ {aFabricar} sob fabricação
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <div className="input-group input-group-sm" style={{ width: 110 }}>
                          <button className="btn btn-outline-secondary"
                            onClick={() => atualizar(item, item.quantidade - 1)}
                            disabled={item.quantidade <= 1}>−</button>
                          <input type="number" className="form-control text-center"
                            value={item.quantidade}
                            onChange={e => atualizar(item, parseInt(e.target.value) || 1)}
                            min="1" />
                          <button className="btn btn-outline-secondary"
                            onClick={() => atualizar(item, item.quantidade + 1)}>+</button>
                        </div>

                        <div style={{ minWidth: 80, textAlign: 'right' }} className="fw-bold">
                          {formatMoeda(preco * item.quantidade)}
                        </div>

                        <button className="btn btn-sm btn-outline-danger py-0 px-2"
                          onClick={() => remover(item.id_carrinho_item)}>✕</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <h6 style={{ color: 'var(--cjc-primary)', marginBottom: 16 }}>Resumo do Pedido</h6>

                <div className="d-flex justify-content-between mb-2" style={{ fontSize: 14 }}>
                  <span className="text-muted">Subtotal</span>
                  <span>{formatMoeda(total)}</span>
                </div>

                {totalFabricar > 0 && (
                  <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 12 }}>
                    <strong>⚠ Atenção:</strong> {totalFabricar} unidade(s) sem estoque entrarão em fabricação.
                    Seu pedido ainda será confirmado normalmente.
                  </div>
                )}

                <hr />

                <div className="d-flex justify-content-between fw-bold mb-3">
                  <span>Total</span>
                  <span style={{ color: 'var(--cjc-primary)', fontSize: 18 }}>{formatMoeda(total)}</span>
                </div>

                <button className="btn btn-primary w-100" onClick={irParaProximoPasso}>
                  Informar Endereço e Confirmar →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
